import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

/** Wizard chrome: the "ledger line" progress rail, the step card and the
 *  Back / Continue controls. */
export const WizardShell = ({
  stepIndex,
  totalSteps,
  step,
  onBack,
  onNext,
  nextLabel = "Continue",
  busy = false,
  canGoBack = true,
  children,
  footerSlot,
  hideNext = false,
}) => {
  const percent = totalSteps > 1 ? Math.round((stepIndex / (totalSteps - 1)) * 100) : 100;

  return (
    <div data-testid="wizard-shell">
      <div className="mb-5">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <p
            data-testid="wizard-step-label"
            className="text-xs font-bold uppercase tracking-[0.14em] text-primary"
          >
            Step {stepIndex + 1} of {totalSteps}
          </p>
          <p className="text-xs font-medium text-muted-foreground">{percent}% complete</p>
        </div>
        <div
          className="tt-ledger-track mt-2.5"
          data-testid="wizard-progress"
          role="progressbar"
          aria-valuenow={percent}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Quote progress"
        >
          <span className="tt-ledger-fill" style={{ width: `${Math.max(percent, 3)}%` }} />
          <span className="tt-ledger-dot" style={{ left: `${Math.max(percent, 3)}%` }} />
        </div>
      </div>

      <div className="tt-card overflow-hidden">
        <div className="border-b border-border bg-[color:var(--tt-surface-2)] px-5 py-5 sm:px-7">
          {step?.chip ? (
            <span className="mb-2 inline-flex items-center rounded-full border border-[color:var(--tt-teal-line)] bg-white px-3 py-1 text-[11px] font-bold uppercase tracking-[0.12em] text-[color:var(--tt-teal-deep)]">
              {step.chip}
            </span>
          ) : null}
          <h2
            data-testid="wizard-step-title"
            className="text-xl font-bold leading-snug text-foreground sm:text-[26px]"
          >
            {step?.title}
          </h2>
          {step?.subtitle ? (
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted-foreground">
              {step.subtitle}
            </p>
          ) : null}
        </div>

        <div
          key={step?.key}
          className="tt-fade-in px-5 py-6 sm:px-7 sm:py-7"
          aria-live="polite"
        >
          {children}
        </div>

        <div className="flex flex-col-reverse gap-3 border-t border-border bg-[color:var(--tt-surface-2)] px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-7">
          <Button
            type="button"
            variant="outline"
            data-testid="wizard-back-button"
            onClick={onBack}
            disabled={!canGoBack || busy}
            className="h-11 w-full bg-white sm:w-auto sm:min-w-[120px]"
          >
            <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
            Back
          </Button>

          {hideNext ? (
            <span />
          ) : (
            <Button
              type="button"
              data-testid="wizard-continue-button"
              onClick={onNext}
              disabled={busy}
              className="h-11 w-full sm:w-auto sm:min-w-[190px]"
            >
              {busy ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
              ) : null}
              {nextLabel}
              {!busy ? <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" /> : null}
            </Button>
          )}
        </div>
      </div>

      {footerSlot}
    </div>
  );
};
